import React, { useState, useEffect } from "react";
import { ImageOff } from "lucide-react";
import { imageUrl } from "../utils/endpoints";
import { useNotification } from "../contexts/NotificationContext";

/**
 * Renders an image that degrades gracefully when the source can't be loaded
 * (e.g. the file was deleted from the server but the DB still references it,
 * or any 404). On error we flip a state flag and render a fallback INSTEAD OF
 * swapping `src` — so the browser never re-attempts the load, which is what
 * previously caused an infinite onError loop and the "stuck / buffering" UI.
 *
 * Props:
 *  - src:           stored/relative or absolute image URL (resolved via imageUrl()).
 *  - alt, className: passed through to the <img>.
 *  - fallback:      "skip" (render nothing) | "note" (small "Image not available"
 *                   placeholder sized to `className`). Default "skip".
 *  - notifyOnError: show a single error toast when the image fails. Default false.
 *  - ...rest:       extra <img> props (onClick, loading, etc.).
 */
const SafeImage = ({
  src,
  alt,
  className,
  fallback = "skip",
  notifyOnError = false,
  ...rest
}) => {
  const [hasError, setHasError] = useState(false);
  const { showToast } = useNotification();

  const resolved = imageUrl(src);

  // Re-attempt when the source changes (e.g. navigating between lightbox images).
  useEffect(() => {
    setHasError(false);
  }, [resolved]);

  const handleError = () => {
    if (notifyOnError) {
      try {
        showToast("Image not found or removed", "error");
      } catch (err) {
        /* toast is best-effort */
      }
    }
    setHasError(true);
  };

  if (hasError) {
    if (fallback === "note") {
      return (
        <div
          className={`${className || ""} flex flex-col items-center justify-center gap-2 text-gray-400 dark:text-slate-500`}
        >
          <ImageOff className="w-8 h-8" />
          <span className="text-sm">Image not available</span>
        </div>
      );
    }
    // "skip" — render nothing.
    return null;
  }

  return (
    <img
      src={resolved}
      alt={alt}
      className={className}
      onError={handleError}
      {...rest}
    />
  );
};

export default SafeImage;
